import React, { useEffect, useRef, useState } from 'react';
import { ContactForm } from '../types';
import '../styles/Contact.css';

interface FormData {
  name: string;
  email: string;
  phone: string;
  company: string;
  projectType: string;
  budget: string;
  timeline: string;
  message: string;
  newsletter: boolean;
  urgency: string;
  preferredContact: string;
}

const Contact: React.FC = () => {
  const contactRef = useRef<HTMLElement>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    budget: '',
    timeline: '',
    message: '',
    newsletter: false,
    urgency: 'normal',
    preferredContact: 'email'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('contact--animated');
          }
        });
      },
      { threshold: 0.1 }
    );

    if (contactRef.current) {
      observer.observe(contactRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formRef.current?.checkValidity()) {
      formRef.current?.reportValidity();
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      if ('localStorage' in window) {
        const submissionData = {
          ...formData,
          submittedAt: new Date().toISOString(),
          userAgent: navigator.userAgent,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
        };
        
        localStorage.setItem('griffin-contact-submission', JSON.stringify(submissionData));
      }

      await new Promise(resolve => setTimeout(resolve, 2000));
      
      setSubmitStatus('success');
      
      setFormData({
        name: '',
        email: '',
        phone: '',
        company: '',
        projectType: '',
        budget: '',
        timeline: '',
        message: '',
        newsletter: false,
        urgency: 'normal',
        preferredContact: 'email'
      });

      if ('Notification' in window && Notification.permission === 'granted') {
        new Notification('Form Submitted!', {
          body: 'Thank you for your message. I\'ll get back to you soon!',
          icon: '/assets/icons/favicon-192x192.png',
          tag: 'contact-form'
        });
      }

    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeolocationRequest = () => {
    if ('geolocation' in navigator) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('User location for better service:', {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          });
        },
        (error) => {
          console.log('Geolocation not available:', error.message);
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    }
  };

  return (
    <section id="contact" className="contact" ref={contactRef}>
      <div className="container">
        <div className="contact__content">
          <div className="contact__info">
            <h2 className="contact__title">Let's Work Together</h2>
            <p className="contact__subtitle">
              Ready to bring your ideas to life? I'd love to hear about your project 
              and discuss how we can create something amazing together.
            </p>

            <div className="contact__details">
              <div className="contact__detail">
                <div className="contact__detail-icon">📧</div>
                <div>
                  <h4>Email</h4>
                  <p>griffincancode@gmail.com</p>
                </div>
              </div>
              
              <div className="contact__detail">
                <div className="contact__detail-icon">📱</div>
                <div>
                  <h4>Phone</h4>
                  <p>+1 (555) 123-4567</p>
                </div>
              </div>
              
              <div className="contact__detail">
                <div className="contact__detail-icon">📍</div>
                <div>
                  <h4>Location</h4>
                  <p>San Francisco, CA</p>
                </div>
              </div>
            </div>

            <div className="contact__availability">
              <h4>Current Availability</h4>
              <div className="contact__status">
                <div className="contact__status-indicator"></div>
                <span>Available for new projects</span>
              </div>
              <p>Typical response time: 24 hours</p>
            </div>
          </div>

          <div className="contact__form-container">
            <form 
              ref={formRef}
              className="contact__form"
              onSubmit={handleSubmit}
              noValidate
              aria-labelledby="form-title"
            >
              <h2 id="form-title" className="contact__form-title">Project Details</h2>

              <fieldset className="contact__fieldset">
                <legend>Contact Information</legend>
                
                <div className="contact__form-group">
                  <label htmlFor="name" className="contact__label">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    aria-describedby="name-hint"
                    className="contact__input"
                    autoComplete="name"
                    minLength={2}
                    maxLength={100}
                  />
                  <div id="name-hint" className="contact__hint">
                    Your full name or how you'd like to be addressed
                  </div>
                </div>

                <div className="contact__form-row">
                  <div className="contact__form-group">
                    <label htmlFor="email" className="contact__label">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      aria-describedby="email-hint"
                      className="contact__input"
                      autoComplete="email"
                      placeholder="you@example.com"
                    />
                    <div id="email-hint" className="contact__hint">
                      We'll use this to send project updates and respond to your inquiry
                    </div>
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="phone" className="contact__label">
                      Phone Number
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      aria-describedby="phone-hint"
                      className="contact__input"
                      autoComplete="tel"
                      placeholder="+1 (555) 123-4567"
                      pattern="[\+]?[0-9\s\-\(\)]+"
                    />
                    <div id="phone-hint" className="contact__hint">
                      Optional - for urgent project discussions
                    </div>
                  </div>
                </div>

                <div className="contact__form-group">
                  <label htmlFor="company" className="contact__label">
                    Company/Organization
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="contact__input"
                    autoComplete="organization"
                    maxLength={100}
                  />
                </div>
              </fieldset>

              <fieldset className="contact__fieldset">
                <legend>Project Information</legend>
                
                <div className="contact__form-row">
                  <div className="contact__form-group">
                    <label htmlFor="projectType" className="contact__label">
                      Project Type *
                    </label>
                    <select
                      id="projectType"
                      name="projectType"
                      value={formData.projectType}
                      onChange={handleInputChange}
                      required
                      className="contact__select"
                      aria-describedby="project-hint"
                    >
                      <option value="">Select project type</option>
                      <option value="website">Website Development</option>
                      <option value="webapp">Web Application</option>
                      <option value="mobile">Mobile App</option>
                      <option value="ecommerce">E-commerce Platform</option>
                      <option value="consulting">Technical Consulting</option>
                      <option value="maintenance">Website Maintenance</option>
                      <option value="other">Other</option>
                    </select>
                    <div id="project-hint" className="contact__hint">
                      Choose the type that best describes your project
                    </div>
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="budget" className="contact__label">
                      Budget Range
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="contact__select"
                    >
                      <option value="">Select budget range</option>
                      <option value="under-5k">Under $5,000</option>
                      <option value="5k-15k">$5,000 - $15,000</option>
                      <option value="15k-50k">$15,000 - $50,000</option>
                      <option value="50k-plus">$50,000+</option>
                      <option value="discuss">Prefer to discuss</option>
                    </select>
                  </div>
                </div>

                <div className="contact__form-row">
                  <div className="contact__form-group">
                    <label htmlFor="timeline" className="contact__label">
                      Project Timeline
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleInputChange}
                      className="contact__select"
                    >
                      <option value="">Select timeline</option>
                      <option value="asap">ASAP (Rush job)</option>
                      <option value="1-month">Within 1 month</option>
                      <option value="2-3-months">2-3 months</option>
                      <option value="3-6-months">3-6 months</option>
                      <option value="6-months-plus">6+ months</option>
                      <option value="flexible">Flexible</option>
                    </select>
                  </div>

                  <div className="contact__form-group">
                    <label htmlFor="urgency" className="contact__label">
                      Priority Level
                    </label>
                    <div className="contact__radio-group" role="radiogroup" aria-labelledby="urgency">
                      <label className="contact__radio-label">
                        <input
                          type="radio"
                          name="urgency"
                          value="low"
                          checked={formData.urgency === 'low'}
                          onChange={handleInputChange}
                        />
                        <span>Low</span>
                      </label>
                      <label className="contact__radio-label">
                        <input
                          type="radio"
                          name="urgency"
                          value="normal"
                          checked={formData.urgency === 'normal'}
                          onChange={handleInputChange}
                        />
                        <span>Normal</span>
                      </label>
                      <label className="contact__radio-label">
                        <input
                          type="radio"
                          name="urgency"
                          value="high"
                          checked={formData.urgency === 'high'}
                          onChange={handleInputChange}
                        />
                        <span>High</span>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="contact__form-group">
                  <label htmlFor="message" className="contact__label">
                    Project Description *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="contact__textarea"
                    aria-describedby="message-hint"
                    placeholder="Tell me about your project, goals, and any specific requirements..."
                    minLength={10}
                    maxLength={2000}
                  />
                  <div id="message-hint" className="contact__hint">
                    Provide as much detail as possible about your project (minimum 10 characters)
                  </div>
                  <div className="contact__char-count" aria-live="polite">
                    {formData.message.length}/2000 characters
                  </div>
                </div>
              </fieldset>

              <fieldset className="contact__fieldset">
                <legend>Communication Preferences</legend>
                
                <div className="contact__form-group">
                  <label htmlFor="preferredContact" className="contact__label">
                    Preferred Contact Method
                  </label>
                  <select
                    id="preferredContact"
                    name="preferredContact"
                    value={formData.preferredContact}
                    onChange={handleInputChange}
                    className="contact__select"
                  >
                    <option value="email">Email</option>
                    <option value="phone">Phone Call</option>
                    <option value="video">Video Call</option>
                    <option value="text">Text Message</option>
                  </select>
                </div>

                <div className="contact__form-group">
                  <label className="contact__checkbox-label">
                    <input
                      type="checkbox"
                      name="newsletter"
                      checked={formData.newsletter}
                      onChange={handleInputChange}
                      className="contact__checkbox"
                    />
                    <span className="contact__checkbox-text">
                      Subscribe to newsletter for project updates and tech insights
                    </span>
                  </label>
                </div>
              </fieldset>

              <div className="contact__form-actions">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className={`contact__submit ${isSubmitting ? 'contact__submit--loading' : ''}`}
                  aria-describedby="submit-status"
                >
                  {isSubmitting ? (
                    <>
                      <span aria-hidden="true">⏳</span>
                      Sending Message...
                    </>
                  ) : (
                    <>
                      <span aria-hidden="true">🚀</span>
                      Send Message
                    </>
                  )}
                </button>

                <div 
                  id="submit-status" 
                  className="contact__status"
                  aria-live="polite"
                  role="status"
                >
                  {submitStatus === 'success' && (
                    <div className="contact__status--success">
                      ✅ Message sent successfully! I'll get back to you soon.
                    </div>
                  )}
                  {submitStatus === 'error' && (
                    <div className="contact__status--error">
                      ❌ Something went wrong. Please try again or email me directly.
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact; 